import classNames from "classnames";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import React from "react";

interface TableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  children?: React.ReactNode;
  scrollAreaRef?: React.RefObject<HTMLDivElement>;
  scrollable?: boolean;
  scrollAreaClassName?: string;
}

interface THeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children?: React.ReactNode;
}

interface TBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  children?: React.ReactNode;
}

interface THProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
}

interface TDProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
  children?: React.ReactNode;
}

interface TRProps extends React.HTMLAttributes<HTMLTableRowElement> {
  children?: React.ReactNode;
}

const TableRoot = ({
  children,
  scrollable = false,
  className,
  scrollAreaClassName,
  scrollAreaRef,
  ...props
}: TableProps) => {
  // Extract all pl-* content with qualifiers using regex
  const extractPaddingLeft = (classString?: string): string | undefined => {
    if (!classString) return undefined;
    const plMatches = classString.match(/(?:^|\s)([^\s]*pl-[^\s]+)/g);
    return plMatches
      ? plMatches.map((match) => match.trim()).join(" ")
      : undefined;
  };

  return scrollable ? (
    <ScrollArea
      ref={scrollAreaRef}
      orientation="horizontal"
      className={className}
      contentClassName={scrollAreaClassName}
      scrollBarClassName={extractPaddingLeft(scrollAreaClassName)}
    >
      <table
        {...props}
        className="border-collapse table-auto leading-normal border-b !my-0 w-full"
      >
        {children}
      </table>
    </ScrollArea>
  ) : (
    <table
      {...props}
      className={classNames(
        "border-collapse table-auto leading-normal border-b !my-0 w-full",
        className
      )}
    >
      {children}
    </table>
  );
};

const THead = ({ children, className, ...props }: THeadProps) => (
  <thead
    {...props}
    className={classNames("border-b border-border-emphasis", className)}
  >
    {children}
  </thead>
);

const TBody = ({ children, className, ...props }: TBodyProps) => (
  <tbody {...props} className={className}>
    {children}
  </tbody>
);

const TH = ({ children, className, ...props }: THProps) => (
  <th
    {...props}
    className={classNames(
      "pl-0 pt-0 pr-9 pb-4 text-left sm-md text-fg-base",
      className
    )}
  >
    {children}
  </th>
);

const TD = ({ children, className, ...props }: TDProps) => (
  <td
    {...props}
    className={classNames(
      "pl-0 pr-9 py-4 align-top [&:has(.research-gap-heatmap-cell)]:p-0 [&:has(.research-gap-heatmap-cell)]:h-px sm-normal text-fg-base",
      className
    )}
  >
    {children}
  </td>
);

const TR = React.forwardRef<HTMLTableRowElement, TRProps>(
  ({ children, className, ...props }, ref) => (
    <tr
      {...props}
      ref={ref}
      className={classNames("border-b border-border-base", className)}
    >
      {children}
    </tr>
  )
);

TR.displayName = "TR";

// Compound component pattern
const Table = Object.assign(TableRoot, {
  THead,
  TBody,
  TH,
  TD,
  TR,
});

export default Table;
